<template>
  <div id="page" class="container-fluid">
    <div class="row header mx-4 my-4">
      <div class="col my-3">
          <h1>Participating Provider Dashboard</h1>
        <div class="col">
          <b-alert v-if="generalErrorMessage" :show="true" fade variant="danger">
            {{ generalErrorMessage }}
          </b-alert>
        </div>
      </div>
      </div>
    <div class="container-fluid">
      <div class="row payee-summary-header pt-2">
        <div class="col-6">
          <h5>SUMMARY</h5>
        </div>
        <div class="col-6">
          <h5>ACCOUNTS</h5>
        </div>
      </div>
      <div class="row payee-summary py-2">
        <div class="col-6">
          <b-skeleton-wrapper :loading="isLoading">
            <template #loading>
              <span class="field-label">Provider/Facility Name: </span><b-skeleton width="35%"></b-skeleton><br>
              <span class="field-label">Name: </span><b-skeleton width="35%"></b-skeleton><br>
              <span class="field-label">E-Mail: </span><b-skeleton width="35%"></b-skeleton><br>
            </template>
            <span class="field-label">Provider/Facility Name: </span>{{ payee.businessName }}<br>
            <span class="field-label">Name: </span>{{ defaultUser.firstName + ' ' + defaultUser.lastName }}<br>
            <span class="field-label">E-Mail: </span>{{ defaultUser.emailAddress }}<br>
          </b-skeleton-wrapper>
        </div>
        <div class="col-6">
          <div class="mb-2" v-for="account in payee.accounts" :key="account.organizationAccountId">
            <span class="field-label"> {{ account.name }}:</span>
            <span class="mr-2">
                  ending with {{ account.last4 }}
            </span>
                  <span v-if="account.status === 'Verified'" class="pt-2"><font-awesome-icon :icon="[ 'far', 'check-circle']" class="orange mr-1"/><b>Verified</b> </span>
                  <span v-else><button @click.prevent="showAccountVerificationModal(account)" class="btn btn-outline-orange">Verify</button></span>
          </div>
          <div class="mb-2">
          <button @click.prevent="addAccount()" class="btn btn-outline-orange m-1 mt-2">
            <font-awesome-icon class="orange mr-1" icon="plus"/>
            <font-awesome-icon class="orange mr-1" :icon="['far', 'building-columns']"/>
            Account
          </button>
            </div>
        </div>
      </div>
      <div class="row">
          <h5 class="my-4">PAYMENTS</h5>
      <div class="container-fluid">
      <div class="row data-table-header">
        <div class="col-1">Date</div>
        <div class="col-1">Account</div>
        <div class="col-2">Patient</div>
        <div class="col-2">Episode</div>
        <div class="col-2">Amount Paid</div>
        <div class="col-4"></div>
      </div>
      <div class="row data-table-row" v-for="transaction in payee.transactions" :key="transaction.organizationBundlePaymentDistributionTransactionId">
        <div class="col-12">
          <div class="row align-items-center">
        <div class="column align-middle col-1">{{ formatDate(transaction.createdAt) }}</div>
        <div class="column align-middle col-1">{{ transaction.destinationAccountName }}</div>
        <div class="column align-middle col-2">{{ transaction.patientName }}</div>
        <div class="column align-middle col-2">{{ transaction.bundleName }}</div>
        <div class="column align-middle col-2">$ {{ formatNumber(-transaction.amount) }}</div>
            <div class="column col-4 text-right">
              <template>
                <button @click.prevent="uploadPayeeFile(transaction)" class="btn btn-outline-orange m-1">
                  <font-awesome-icon class="orange mr-1" :icon="['far', 'file-upload']"/>Upload File
                </button>
                <button v-if="hasMatchingDocuments(transaction.organizationBundlePaymentDistributionId)" @click.prevent="viewPayeeFile(transaction.organizationBundlePaymentDistributionId)" class="btn btn-outline-orange m-1">
                  <font-awesome-icon class="orange mr-1" :icon="['far', 'folder-arrow-down']" />View Files
                </button>
                <button @click.prevent="showReceipt(transaction)" class="btn btn-outline-orange m-1">
                  <font-awesome-icon class="orange mr-1" :icon="['far', 'file-invoice-dollar']"/>Download Receipt
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>
        <div class="alert alert-secondary" role="alert" v-show="!payee.transactions || !payee.transactions.length">
          &nbsp;&nbsp;No transactions on file.
        </div>
        </div>
      </div>
    </div>
    <pdf-view-modal :title="pdfTitle" :url="pdfUrl" :visible="showingPdfModal"
                    @update:visible="visible => showingPdfModal = visible"></pdf-view-modal>
    <account-modal :visible="showingAccountModal" :account-payee="payee" @update:visible="visible => showingAccountModal = visible" @accountSaved="accountSaved()"></account-modal>
    <micro-deposit-verification-modal :visible="showingAccountDepositsModal" :account-payee="payee" :account="selectedAccount" @update:visible="visible => showingAccountDepositsModal = visible" @accountVerified="accountVerified()"></micro-deposit-verification-modal>
    <account-needs-verification-modal :visible="showingAccountVerificationModal" :account-payee="payee" :account="selectedAccount" @update:visible="visible => showingAccountVerificationModal = visible" @accountVerificationStarted="accountVerificationStarted()"></account-needs-verification-modal>
    <loading-modal :visible="showingLoadingModal" @update:visible="visible => showingLoadingModal = visible"></loading-modal>
    <payee-file-upload-modal :payment-distribution-id="uploadingPayeeFile.organizationBundlePaymentDistributionId" @updated="fileUploaded" :visible="showingPayeeFileUploadModal" @update:visible="visible => showingPayeeFileUploadModal = visible"
    ></payee-file-upload-modal>
    <payee-view-file-modal :payment-distribution-documents="filteredDocuments"
                           :organization-bundle-payment-distribution-id="organizationBundlePaymentDistributionId"
                           :visible="showingPayeeViewFileModal" @update:visible="visible => showingPayeeViewFileModal = visible"
    ></payee-view-file-modal>
  </div>
</template>

<script>
  import api from '@/services/api'
  import ErrorsMixin from '@/mixins/ErrorsMixin'
  import AccountModal from '@/components/AccountModal'
  import MicroDepositVerificationModal from '@/components/MicroDepositVerificationModal'
  import AccountNeedsVerificationModal from '@/components/AccountNeedsVerificationModal'
  import moment from 'moment'
  import PdfViewModal from '@/components/PdfViewModal'
  import LoadingModal from '@/components/LoadingModal'
  import AnalyticsMixin from '@/mixins/AnalyticsMixin'
  import PayeeFileUploadModal from '@/components/PayeeFileUploadModal.vue'
  import PayeeViewFileModal from '@/components/PayeeViewFileModal.vue'

  export default {
    name: 'PayeeDashboard',

    mixins: [ErrorsMixin, AnalyticsMixin],

    components: { PayeeViewFileModal, PayeeFileUploadModal, AccountModal, MicroDepositVerificationModal, AccountNeedsVerificationModal, PdfViewModal, LoadingModal },

    props: {
      id: {
        default: 0
      }
    },

    data () {
      return {
        payee: {
          organizationPayeeId: 0,
          businessName: '',
          payments: [],
          users: [],
          paymentDistributionDocuments: [],
          transactions: []
        },
        defaultUser: {
          emailAddress: '',
          firstName: '',
          lastName: ''
        },
        showingPdfModal: false,
        organizationBundlePaymentDistributionId: 0,
        pdfUrl: '',
        pdfTitle: '',
        showingAccountModal: false,
        showingPayeeFileUploadModal: false,
        showingAccountVerificationModal: false,
        showingAccountDepositsModal: false,
        showingLoadingModal: false,
        showingPayeeViewFileModal: false,
        selectedAccount: {
          externalId: ''
        },
        isLoading: false,
        uploadingPayeeFile: {
          organizationBundlePaymentDistributionId: 0
        },
        filteredDocuments: []
      }
    },

    watch: {
      // call again the method if the route changes
      $route: 'loadPayee'
    },

    computed: {
      isOrgAdmin () {
        return this.$store.isOrgAdmin
      }
    },

    methods: {

      loadPayee () {
        this.isLoading = true

        api.get('/Dashboard/PayeeDetails').then(response => {
          this.payee = response.data
          const payeeUser = this.payee.users.find(user => user.user.userId === this.$store.user.userId)
          this.defaultUser = payeeUser.user
          this.isLoading = false
        }).catch(error => {
          this.isLoading = false
          if (!this.$store.canHandleNetworkError(error)) {
            this.setFromApiErrorResponse(error)
            this.$log.error('Error attempting to load payee details: ' + error.message, error)
          }
        })
      },

      showReceipt (transaction) {
        this.showingLoadingModal = true
        this.pdfTitle = 'Receipt for ' + transaction.bundleName
        api.get('/Dashboard/PaymentDistribution/' + transaction.organizationBundlePaymentDistributionId + '/Payee/' + this.payee.organizationPayeeId + '/Download', {
          responseType: 'blob',
          headers: { 'Content-Type': 'application/pdf' }
        }).then(response => {
          this.trackEvent('Transaction', 'Receipt', 'ResponseTime', response.config.requestDuration)
          this.trackEvent('Transaction', 'Receipt', 'Show', transaction.organizationBundlePaymentDistributionId)

          if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(response.data, 'PayeeInvoice.pdf')
          } else {
            this.pdfUrl = URL.createObjectURL(response.data)
            this.showingPdfModal = true
          }
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.trackEvent('Transaction', 'Receipt', error.message)
            this.$log.error('Error attempting to load receipt: ' + error.message, error)
            this.errors = ['Error loading receipt: ' + error.message]
          }
        }).then(() => {
          // always executed
          this.showingLoadingModal = false
        })
      },

      addAccount () {
        this.showingAccountModal = true
      },

      uploadPayeeFile (transaction) {
        this.uploadingPayeeFile = transaction
        this.showingPayeeFileUploadModal = true
      },
      hasMatchingDocuments (organizationBundlePaymentDistributionId) {
        return this.payee.paymentDistributionDocuments.some(
          (doc) =>
            doc.organizationBundlePaymentDistributionId ===
            organizationBundlePaymentDistributionId
        )
      },
      viewPayeeFile (organizationBundlePaymentDistributionId) {
        this.filteredDocuments = this.payee.paymentDistributionDocuments.filter(
          (doc) =>
            doc.organizationBundlePaymentDistributionId ===
            organizationBundlePaymentDistributionId
        )
        this.organizationBundlePaymentDistributionId = organizationBundlePaymentDistributionId
        this.showingPayeeViewFileModal = true
      },

      fileUploaded () {
        this.showingPayeeFileUploadModal = false
        this.loadPayee()
      },

      accountSaved () {
        this.showingAccountModal = false
        this.loadPayee()
      },

      accountVerified () {
        this.showingAccountDepositsModal = false
        this.selectedAccount = { externalId: '' }
        this.loadPayee()
      },

      accountVerificationStarted () {
        this.showingAccountVerificationModal = false
        this.selectedAccount = { externalId: '' }
        this.loadPayee()
      },

      showAccountVerificationModal (account) {
        this.selectedAccount = account

        if (account.microDepositStatus) {
          this.isLoading = true

          const request = {
            fundingSourceId: account.externalId
          }

          api.post('/Dashboard/Payee/Accounts/VerificationDetails', request).then(response => {
            this.isLoading = false
            account.microDepositStatus = response.data.status

            if (account.microDepositStatus === 'Processed') { // deposits are complete, ready to verify
              this.showingAccountDepositsModal = true
            } else if (account.microDepositStatus === 'Pending') {
              this.showingAccountDepositsModal = true
            } else {
              this.showingAccountVerificationModal = true
            }
          }).catch(error => {
            this.isLoading = false
            if (!this.$store.canHandleNetworkError(error)) {
              this.setFromApiErrorResponse(error)
              this.$log.error('Error attempting to load payee verification details: ' + error.message, error)
            }
          })
        } else {
          this.showingAccountVerificationModal = true
        }
      },

      formatDate (date) {
        if (date) {
          return moment(date).format('MM/DD/YYYY')
        } else {
          return ''
        }
      },

      formatNumber (number) {
        if (number === 0 || number === 0.0) {
          return '0.00'
        }
        if (number) {
          return number.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
        }

        return ''
      }
    },

    created () {
      this.loadPayee()
    }
  }
</script>

<style scoped>

.payee-summary-header {
  border-top: 1px solid #FFA800;
  background-color: #f9f9f9;
}

.payee-summary-header h5 {
  color: #001859;
}

.payee-summary .column {
  padding: 0.75rem;
  vertical-align: bottom;
  border-collapse: collapse;
}

.payee-summary {
  border-top: 1px solid #FFA800;
  border-bottom: 1px solid #dee2e6;
  background-color: #f9f9f9;
  font-size: 1.2em;
  /*cursor: pointer;*/
}

.header-row div {
  padding: 0.75rem;
  vertical-align: bottom;
  border-top: 1px solid #dee2e6;
  border-bottom: 2px solid #dee2e6;
  font-weight: bold;
  border-collapse: collapse;
}

.data-row {
  min-height: 60px;
}

.b-skeleton-text {
  display: inline-block;
  vertical-align: middle;
}
</style>
